import React from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import type { Customer } from '../../../types/backend';
import type { AccountEntity } from '../hooks/useAccountForm';
import { useAccountBaselineStatus } from '../hooks/useAccountBaselineStatus';
import { externalIdNote, reachabilityText } from '../accountTabs';

/** Read from the status the onboarding panel already loads; only mounted while account_baseline is on. */
const Reachability: React.FC<{ customerId: string; awsAccountId: string }> = ({ customerId, awsAccountId }) => {
  const { accounts, loading, error } = useAccountBaselineStatus(customerId);
  const view = accounts.find((a) => a.aws_account_id === awsAccountId);
  // Three different facts, never one fallback: the read failed, is still
  // running, or came back without this account.
  const text = error ? 'Could not read' : view ? reachabilityText(view) : loading ? 'Reading' : 'Not checked yet';
  return (
    <Box>
      <Typography component="dt" variant="caption" color="text.secondary">Reachability</Typography>
      <Typography component="dd" variant="body2" sx={{ m: 0 }}>{text}</Typography>
    </Box>
  );
};

export interface AccountAccessTabProps {
  customer: Customer;
  account: AccountEntity;
  baselineEnabled: boolean;
}

/** The roles Cloud2 uses in the account, as the account record names them. */
const AccountAccessTab: React.FC<AccountAccessTabProps> = ({ customer, account, baselineEnabled }) => {
  const roles: Array<[string, string | undefined]> = [
    ['Read-only role', account.read_role_name],
    ['Write role', account.write_role_name],
    ['Automation role', account.automation_role_name],
    ['Onboarding role', account.onboarding_role_name],
  ];

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2, maxWidth: 820, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" aria-label="Roles">
          <TableHead>
            <TableRow>
              <TableCell>Role</TableCell>
              <TableCell>Role name</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.map(([use, name]) => (
              <TableRow key={use}>
                <TableCell>{use}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}>{name || 'Not set'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Typography variant="body2">
        Operations account {customer.operations_account_id || 'not configured'}
      </Typography>

      <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="dt" variant="caption" color="text.secondary">External ID</Typography>
          <Typography component="dd" variant="body2" sx={{ m: 0, overflowWrap: 'anywhere' }}>
            {account.external_id ? (
              <>
                <Box component="span" sx={{ fontFamily: 'monospace' }}>{account.external_id}</Box>{' '}
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  {externalIdNote(account.external_id, customer.operations_account_external_id)}
                </Box>
              </>
            ) : 'Not set'}
          </Typography>
        </Box>
        {baselineEnabled && <Reachability customerId={customer.id} awsAccountId={account.aws_account_id} />}
      </Box>
    </Box>
  );
};

export default AccountAccessTab;
